/* ---------------------------------------------------------------------------
   Construction Scheduler's Baseline page: the programme in force, the revision
   waiting for acceptance, the contract, and the history (LC-40, LC-42, LC-43).

   A planner takes or revises a baseline, with a reason for a revision; an
   approver who did not take it accepts it; only the accepted one is measured
   against. The contract header rides on each baseline, and the extensions of
   time on the contract. Every panel here is `panel()`.
   --------------------------------------------------------------------------- */

import { h, ICONS, type Child } from '../core/dom.ts';
import { actions, can, opensWorks, whyNot, type State } from '../core/store.ts';
import { WORKS_KINDS, WORKS_KIND_LABEL } from '../data/phase.ts';
import { panel, panelButton } from '../components/panel.ts';
import { button, emptyState, fact, field, scrim, tag } from '../components/widgets.ts';
import { findBridge, prettyDate } from '../data/derive.ts';
import { weekLabel, type WorkCalendar } from '../data/calendar.ts';
import {
  EXTENSION_STATE_LABEL,
  completionDate,
  grantedDays,
  type ContractExtension,
  type ContractHeader,
} from '../data/contract.ts';
import { normaliseIdentity } from '../data/roles.ts';
import { formatMoney } from '../tenant/format.ts';
import { active } from '../tenant/registry.ts';
import type { Baseline } from '../data/variance.ts';

/** Whether the signed-in person made a record — the client's echo of `assertNotOwnWork`. */
function mine(s: Readonly<State>, author: string | null | undefined): boolean {
  const me = s.identity?.author ?? null;
  return !!me && !!author && normaliseIdentity(me) === normaliseIdentity(author);
}

function calendarName(s: Readonly<State>, b: Baseline): string {
  const c = b.calendar ?? s.calendars.find((k) => k.id === b.calendarId) ?? null;
  return c ? `${c.name} · ${weekLabel(c.week)}` : 'Every day';
}

/** Money in the contract's own currency, where it is the department's; the code otherwise. */
function contractValue(c: ContractHeader): string {
  if (c.value === null) return '—';
  const own = active().currency;
  return c.currency === '' || c.currency === own.code
    ? formatMoney(c.value, own)
    : `${c.currency} ${c.value.toLocaleString('en')}`;
}

/** The page. */
export function baselinePage(bridgeName: string, s: Readonly<State>): HTMLElement {
  const mayManage = can(s, 'programme.manage');
  const parts: Child[] = [];

  if (s.baselineNote && !s.baselineForm && !s.eotDraft) {
    parts.push(h('div', { class: 'dlg__note' }, h('span', { text: s.baselineNote })));
  }
  if (s.proposedBaseline) parts.push(proposedPanel(s, s.proposedBaseline));
  if (s.baseline) parts.push(inForcePanel(s, s.baseline));

  if (!s.baseline && !s.proposedBaseline) {
    parts.push(
      panel(
        { title: 'Baseline', collapsible: 'baseline.none' },
        emptyState('No baseline.'),
        mayManage
          ? h(
              'div',
              { class: 'plan__acts' },
              button(opensWorks(s) ? 'Take a works baseline' : 'Take a baseline', {
                primary: true,
                iconPath: ICONS.plus,
                title: opensWorks(s)
                  ? `Programme works on ${bridgeName}: accepting it opens the works and the site record`
                  : `Freeze the programme from ${bridgeName}’s 3D model, with its contract`,
                onClick: () => actions.openBaselineForm(),
              }),
            )
          : null,
      ),
    );
  }

  const contract = s.baseline?.contract ?? s.proposedBaseline?.contract ?? null;
  if (contract) parts.push(contractPanel(s, contract, !!s.baseline?.contract));
  if (s.baselineHistory.length > 0) parts.push(historyPanel(s));

  return h('div', { class: 'plan plan--baseline' }, ...parts);
}

/* --------------------------- in force, and waiting --------------------------- */

function spanOf(b: Baseline): { start: string | null; finish: string | null } {
  const starts = b.activities.map((a) => a.plannedStart).filter(Boolean).sort();
  const finishes = b.activities.map((a) => a.plannedFinish).filter(Boolean).sort();
  return { start: starts[0] ?? null, finish: finishes.at(-1) ?? null };
}

/**
 * The Baseline page's header control: Edit baseline, which opens the revision
 * dialog. Only once there is a baseline to revise — before that the page's own
 * Take a baseline is the way in.
 */
export function baselineHeadActions(s: Readonly<State>, bridgeName: string): HTMLElement[] {
  if (s.baseline === null && s.proposedBaseline === null) return [];
  const ok = can(s, 'programme.manage');
  return [
    panelButton({
      icon: ICONS.edit,
      text: 'Edit baseline',
      disabled: !ok,
      title: ok
        ? `Take a revised programme of ${bridgeName}, with the reason for it. The baseline in force stays until the revision is accepted.`
        : whyNot(s, 'Revising the baseline', 'programme.manage'),
      onclick: () => actions.openBaselineForm(),
    }),
  ];
}

/**
 * The Baseline page's foot while a revision is waiting: Accept baseline, the
 * approver's, and never the one who took it (LC-40).
 */
export function baselineFoot(s: Readonly<State>): HTMLElement[] {
  const p = s.proposedBaseline;
  if (p === null) return [];
  const own = mine(s, p.takenBy);
  const mayApprove = can(s, 'programme.approve');
  return [
    button(s.baselineBusy && s.baselineForm === null ? 'Accepting…' : 'Accept baseline', {
      primary: true,
      iconPath: ICONS.check,
      disabled: own || !mayApprove || s.baselineBusy,
      title: own
        ? 'You took this baseline, so somebody else must accept it.'
        : !mayApprove
          ? whyNot(s, 'Accepting a baseline', 'programme.approve')
          : `Make ${p.name} the programme measured against`,
      onClick: () => void actions.acceptProposedBaseline(),
    }),
  ];
}

/** Calendar days from one ISO day to another; negative when `to` is earlier. */
function dayGap(from: string, to: string): number {
  return Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function dayCount(n: number): string {
  return `${n} ${Math.abs(n) === 1 ? 'day' : 'days'}`;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** One of the figures across the top of the baseline in force. */
function tile(label: string, value: string, sub: string | null, tone?: 'good' | 'late'): HTMLElement {
  return h(
    'div',
    { class: ['bl__tile', tone && `bl__tile--${tone}`] },
    h('span', { class: 'bl__tile-k', text: label }),
    h('span', { class: 'bl__tile-v num', text: value }),
    sub ? h('span', { class: 'bl__tile-s', text: sub }) : null,
  );
}

function card(title: string, ...facts: Child[]): HTMLElement {
  return h('div', { class: 'bl__card' }, h('div', { class: 'bl__card-h', text: title }), h('div', { class: 'det-facts' }, ...facts));
}

function inForcePanel(s: Readonly<State>, b: Baseline): HTMLElement {
  const span = spanOf(b);
  const c = b.contract ?? null;
  const granted = grantedDays(s.extensions);
  const completion = c ? completionDate(c, s.extensions) : null;
  const float = completion && span.finish ? dayGap(span.finish, completion) : null;

  return panel(
    { title: 'Baseline in force', collapsible: 'baseline.inForce' },
    h(
      'div',
      { class: 'bl', 'data-baseline': 'in-force' },
      h(
        'div',
        { class: 'bl__head' },
        tag('Accepted', 2),
        h(
          'div',
          { class: 'bl__who' },
          h('span', { text: `Frozen ${prettyDate(b.takenAt.slice(0, 10))} · ${b.takenBy ?? 'not identified'}` }),
          h('span', {
            text: `Accepted ${b.acceptedAt ? prettyDate(b.acceptedAt.slice(0, 10)) : '—'} · ${b.acceptedBy ?? (b.acceptedAt ? 'not identified' : '—')}`,
          }),
        ),
      ),
      b.worksKind || b.reason
        ? h(
            'div',
            { class: 'det-facts bl__extra' },
            b.worksKind ? fact('Works', WORKS_KIND_LABEL[b.worksKind]) : null,
            b.reason ? fact('Reason', b.reason) : null,
          )
        : null,
      h(
        'div',
        { class: 'bl__tiles' },
        c ? null : tile('Planned start', prettyDate(span.start), null),
        tile('Planned finish', prettyDate(span.finish), span.start ? `from ${prettyDate(span.start)}` : null),
        c && completion
          ? tile('Completion date', prettyDate(completion), granted ? `${c.completionDays} d + ${granted} d EOT` : `${c.completionDays} days`)
          : null,
        float === null
          ? null
          : float >= 0
            ? tile('Float to completion', dayCount(float), null, 'good')
            : tile('Overrun', dayCount(-float), null, 'late'),
        c ? tile('Contract value', contractValue(c), null) : null,
      ),
      span.start && span.finish ? timeline(span.start, span.finish, c, s.extensions) : null,
      h(
        'div',
        { class: 'bl__cards' },
        card(
          'Network',
          fact('Activities', String(b.activities.filter((a) => !a.isGroup).length)),
          fact('Links', String(b.links?.length ?? 0)),
          fact('Calendar', calendarName(s, b)),
          fact('Project start', prettyDate(b.projectStart)),
        ),
        c
          ? card(
              'Contract',
              fact('Contractor', c.contractor || '—'),
              fact('Number', c.contractNo || '—'),
              fact('Commencement', prettyDate(c.commencement)),
              fact('Time for completion', `${c.completionDays} days`),
            )
          : null,
      ),
    ),
  );
}

/**
 * The programme on one line: planned work, the float to the completion date
 * (or the overrun past it), the days granted by extensions, the milestones and
 * today — with the milestones listed under it by number.
 */
function timeline(start: string, finish: string, c: ContractHeader | null, extensions: readonly ContractExtension[]): HTMLElement {
  const today = todayIso();
  const completion = c ? completionDate(c, extensions) : null;
  const original = c ? completionDate(c) : null;
  const granted = grantedDays(extensions);
  const lo = c && c.commencement < start ? c.commencement : start;
  const hi = completion && completion > finish ? completion : finish;
  const total = Math.max(1, dayGap(lo, hi));
  const at = (d: string) => Math.min(100, Math.max(0, (dayGap(lo, d) / total) * 100));
  const seg = (cls: string, from: string, to: string) =>
    h('div', { class: ['bl__seg', cls], style: { left: `${at(from)}%`, width: `${Math.max(0.4, at(to) - at(from))}%` } });

  const bars: Child[] = [seg('bl__seg--plan', start, finish)];
  const legend: [string, string][] = [['plan', 'Planned']];
  if (completion && original) {
    if (finish <= completion) {
      const eotFrom = original > finish ? original : finish;
      const floatTo = granted > 0 ? eotFrom : completion;
      if (floatTo > finish) {
        bars.push(seg('bl__seg--float', finish, floatTo));
        legend.push(['float', 'Float']);
      }
      if (granted > 0 && completion > eotFrom) {
        bars.push(seg('bl__seg--eot', eotFrom, completion));
        legend.push(['eot', 'EOT']);
      }
    } else {
      bars.push(seg('bl__seg--late', completion, finish));
      legend.push(['late', 'Overrun']);
    }
  }
  if (today > lo) bars.push(seg('bl__seg--done', lo, today < finish ? today : finish));

  const months: Child[] = [];
  const first = new Date(`${lo}T00:00:00Z`);
  const count = (new Date(`${hi}T00:00:00Z`).getUTCFullYear() - first.getUTCFullYear()) * 12 + new Date(`${hi}T00:00:00Z`).getUTCMonth() - first.getUTCMonth();
  const every = Math.max(1, Math.ceil(count / 10));
  for (let i = 1; i <= count; i += 1) {
    const m = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + i, 1));
    const iso = m.toISOString().slice(0, 10);
    months.push(h('div', { class: 'bl__tick', style: { left: `${at(iso)}%` } }));
    if (i % every === 0) {
      const label = m.getUTCMonth() === 0 ? `Jan ${String(m.getUTCFullYear()).slice(2)}` : MONTH_ABBR[m.getUTCMonth()]!;
      months.push(h('span', { class: 'bl__month', style: { left: `${at(iso)}%` }, text: label }));
    }
  }

  const milestones = (c?.milestones ?? []).filter((m) => m.dueOn >= lo && m.dueOn <= hi);
  const inRange = today >= lo && today <= hi;

  return h(
    'div',
    { class: 'bl__time' },
    h(
      'div',
      { class: 'bl__legend' },
      h('span', { class: 'bl__legend-t', text: 'Programme' }),
      ...legend.map(([k, label]) => h('span', { class: 'bl__key' }, h('i', { class: `bl__swatch bl__swatch--${k}` }), h('span', { text: label }))),
    ),
    h(
      'div',
      { class: 'bl__track' },
      h('span', { class: 'bl__month', style: { left: '0%' }, text: MONTH_ABBR[first.getUTCMonth()]! }),
      ...months,
      ...bars,
      ...milestones.map((m, i) =>
        h(
          'div',
          { class: 'bl__ms', style: { left: `${at(m.dueOn)}%` }, title: `${m.name} · ${prettyDate(m.dueOn)}` },
          h('i', { class: 'bl__diamond' }),
          h('span', { class: 'bl__ms-n num', text: String(i + 1) }),
        ),
      ),
      inRange
        ? h(
            'div',
            { class: 'bl__today', style: { left: `${at(today)}%` } },
            h('span', { class: 'bl__today-l num', text: prettyDate(today) }),
          )
        : null,
    ),
    milestones.length
      ? h(
          'div',
          { class: 'bl__mslist' },
          ...milestones.map((m, i) => {
            const left = dayGap(today, m.dueOn);
            return h(
              'div',
              { class: ['bl__msrow', left < 0 && 'bl__msrow--past'] },
              h('span', { class: 'bl__msrow-k' }, h('b', { class: 'num', text: String(i + 1) }), h('span', { text: m.name })),
              h(
                'span',
                { class: 'bl__msrow-v num' },
                h('span', { text: prettyDate(m.dueOn) }),
                left >= 0 ? h('span', { class: 'muted', text: left === 0 ? ' · today' : ` · in ${left} d` }) : null,
              ),
            );
          }),
        )
      : null,
  );
}

function proposedPanel(s: Readonly<State>, p: Baseline): HTMLElement {
  const span = spanOf(p);
  return panel(
    { title: 'Waiting for acceptance', collapsible: 'baseline.proposed' },
    h('div', { class: 'det-facts', 'data-baseline': 'proposed' },
      fact('State', tag('Proposed', 3)),
      fact('Frozen', prettyDate(p.takenAt.slice(0, 10))),
      fact('By', p.takenBy ?? 'not identified'),
      p.worksKind ? fact('Works', WORKS_KIND_LABEL[p.worksKind]) : null,
      fact('Reason', p.reason ?? '—'),
      fact('Calendar', calendarName(s, p)),
      fact('Activities', String(p.activities.filter((a) => !a.isGroup).length)),
      fact('Planned finish', prettyDate(span.finish)),
    ),
  );
}

/* --------------------------- take or revise --------------------------- */

/** A dialog of this page: its head, the page's note while it is open, a form body and its buttons. */
function dialog(
  s: Readonly<State>,
  kicker: string,
  title: string,
  formAttr: string,
  close: () => void,
  submit: () => void,
  body: Child[],
  submitText: string,
): HTMLElement {
  return h(
    'div',
    { class: 'dlg', ...scrim(close, () => s.baselineBusy) },
    h(
      'form',
      {
        class: 'dlg__panel dlg__panel--sm dlg__panel--compact',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': title,
        'data-form': formAttr,
        onsubmit: (e: Event) => {
          e.preventDefault();
          submit();
        },
      },
      h(
        'div',
        { class: 'dlg__head' },
        h('div', {}, h('div', { class: 'dlg__kicker', text: kicker }), h('h2', { class: 'dlg__title', text: title })),
        h('span', { class: 'spacer' }),
        h('button', { type: 'button', class: 'dlg__close', title: 'Close', 'aria-label': 'Close', onclick: close }, '×'),
      ),
      s.baselineNote ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.baselineNote })) : null,
      h('div', { class: 'dlg__body' }, h('div', { class: 'dlg__form-grid' }, ...body)),
      h(
        'div',
        { class: 'dlg__foot' },
        h('span', { class: 'spacer' }),
        button('Cancel', { onClick: close, disabled: s.baselineBusy }),
        button(submitText, { primary: true, type: 'submit', disabled: s.baselineBusy }),
      ),
    ),
  );
}

/** Take a baseline, or revise it: the dialog, or null when it is closed. */
export function baselineDialog(s: Readonly<State>): HTMLElement | null {
  const f = s.baselineForm;
  if (f === null) return null;
  const b = findBridge(s.selectedId);
  if (b === null) return null;
  /* On a bridge built with no works open, a works baseline (LC-81): it names the works and revises nothing. */
  const works = opensWorks(s);
  const revising = !works && (s.baseline !== null || s.proposedBaseline !== null || s.baselineHistory.length > 0);
  const set = (key: keyof typeof f) => (e: Event) =>
    actions.setBaselineFormField(key, (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value);
  const input = (id: string, key: keyof typeof f, type = 'text', extra: Record<string, string> = {}) =>
    h('input', { id, class: 'input', type, value: f[key], oninput: set(key), ...extra });
  const currency = active().currency;
  const days = Number(f.completionDays);
  const finish =
    /^\d{4}-\d{2}-\d{2}$/.test(f.commencement) && Number.isInteger(days) && days > 0
      ? prettyDate(completionDate({ commencement: f.commencement, completionDays: days }))
      : '—';

  return dialog(
    s,
    `${b.id} · ${b.name}`,
    works ? 'Take a works baseline' : revising ? 'Revise baseline' : 'Take a baseline',
    'baseline',
    () => actions.cancelBaselineForm(),
    () => void actions.captureBaseline(b.id),
    [
      h('div', { style: 'grid-column: 1 / -1' }, field('Name', input('bl-name', 'name'), 'bl-name')),
      works
        ? h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Works',
              h(
                'select',
                { id: 'bl-works', class: 'select', required: true, onchange: set('worksKind') },
                h('option', { value: '', selected: f.worksKind === '', text: 'Choose…' }),
                ...WORKS_KINDS.map((k) => h('option', { value: k, selected: k === f.worksKind, text: WORKS_KIND_LABEL[k] })),
              ),
              'bl-works',
            ),
          )
        : null,
      revising
        ? h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Reason for the revision',
              h('textarea', { id: 'bl-reason', class: 'input', rows: 2, value: f.reason, oninput: set('reason') }),
              'bl-reason',
            ),
          )
        : null,
      h(
        'div',
        { style: 'grid-column: 1 / -1' },
        field(
          'Working calendar',
          h(
            'select',
            { id: 'bl-calendar', class: 'select', onchange: set('calendarId') },
            h('option', { value: '', selected: f.calendarId === '', text: 'Every day' }),
            ...s.calendars.map((c: WorkCalendar) =>
              h('option', {
                value: c.id,
                selected: c.id === f.calendarId,
                text: `${c.name} · ${weekLabel(c.week)}${c.holidays.length ? ` · ${c.holidays.length} holidays` : ''}`,
              }),
            ),
          ),
          'bl-calendar',
        ),
      ),
      field('Contractor', input('bl-contractor', 'contractor'), 'bl-contractor'),
      field('Contract number', input('bl-contract-no', 'contractNo'), 'bl-contract-no'),
      field(`Contract value (${currency.code})`, input('bl-value', 'value', 'text', { inputmode: 'decimal' }), 'bl-value'),
      field('Commencement date', input('bl-commencement', 'commencement', 'date', { required: 'true' }), 'bl-commencement'),
      field(
        'Time for completion (days)',
        input('bl-days', 'completionDays', 'number', { min: '1', step: '1' }),
        'bl-days',
      ),
      field('Completion date', h('input', { class: 'input', disabled: true, value: finish })),
      h(
        'div',
        { style: 'grid-column: 1 / -1' },
        field(
          'Milestones, one per line',
          h('textarea', {
            id: 'bl-milestones',
            class: 'input',
            rows: 3,
            placeholder: '2026-08-15 Substructure complete',
            value: f.milestones,
            oninput: set('milestones'),
          }),
          'bl-milestones',
        ),
      ),
    ],
    s.baselineBusy ? 'Taking…' : revising ? 'Take the revision' : 'Take the baseline',
  );
}

/* --------------------------- the contract --------------------------- */

/**
 * The contract. Where the baseline in force already shows its header — the
 * dates, the value, the contractor — this panel is the extensions of time
 * alone; with only a revision waiting, it shows the header as well.
 */
function contractPanel(s: Readonly<State>, c: ContractHeader, headerShown: boolean): HTMLElement {
  const mayManage = can(s, 'programme.manage');
  const mayApprove = can(s, 'programme.approve');
  const granted = grantedDays(s.extensions);
  return panel(
    {
      title: headerShown ? 'Extensions of time' : 'Contract',
      collapsible: 'baseline.contract',
      actions: mayManage
        ? [
            panelButton({
              icon: ICONS.plus,
              text: 'Extension of time',
              title: 'Claim an extension of time, with its reason and days. An approver decides it.',
              onclick: () => actions.openExtension(),
            }),
          ]
        : [],
    },
    h(
      'div',
      { 'data-contract': c.contractNo },
      headerShown
        ? null
        : h('div', { class: 'det-facts' },
            fact('Contractor', c.contractor || '—'),
            fact('Contract number', c.contractNo || '—'),
            fact('Value', contractValue(c)),
            fact('Commencement', prettyDate(c.commencement)),
            fact('Time for completion', `${c.completionDays} days`),
            fact('Extensions granted', granted ? `${granted} days` : '—'),
            fact('Completion date', prettyDate(completionDate(c, s.extensions))),
            ...c.milestones.map((m) => fact(m.name, prettyDate(m.dueOn))),
          ),
      s.extensions.length
        ? extensionList(s, c, s.extensions, mayApprove)
        : headerShown
          ? h('div', { class: 'eot__none muted', text: 'None claimed.' })
          : null,
    ),
  );
}

/** Claim an extension of time: the dialog, or null when it is closed. */
export function extensionDialog(s: Readonly<State>): HTMLElement | null {
  const d = s.eotDraft;
  if (d === null) return null;
  const c = s.baseline?.contract ?? s.proposedBaseline?.contract ?? null;
  return dialog(
    s,
    c?.contractNo || 'Contract',
    'Extension of time',
    'eot',
    () => actions.cancelExtension(),
    () => void actions.submitExtension(),
    [
      h(
        'div',
        { style: 'grid-column: 1 / -1' },
        field(
          'Reason',
          h('textarea', {
            id: 'eot-reason',
            class: 'input',
            rows: 3,
            value: d.reason,
            oninput: (e: Event) => actions.setExtensionField('reason', (e.target as HTMLTextAreaElement).value),
          }),
          'eot-reason',
        ),
      ),
      field(
        'Days',
        h('input', {
          id: 'eot-days',
          class: 'input',
          type: 'number',
          min: '1',
          step: '1',
          value: d.days,
          oninput: (e: Event) => actions.setExtensionField('days', (e.target as HTMLInputElement).value),
        }),
        'eot-days',
      ),
    ],
    'Claim',
  );
}

/** The extensions of time, one row each: the days, the reason, and its state or the approver's decision. */
function extensionList(s: Readonly<State>, c: ContractHeader, list: readonly ContractExtension[], mayApprove: boolean): HTMLElement {
  return h(
    'div',
    { class: 'eot' },
    ...list.map((e) => {
      const ifGranted =
        e.state === 'proposed'
          ? ` · if granted, completion ${prettyDate(completionDate(c, [...list, { state: 'accepted', days: e.days }]))}`
          : '';
      return h(
        'div',
        { class: ['eot__row', `eot__row--${e.state}`], 'data-eot': e.state },
        h('span', { class: 'eot__days num' }, h('b', { text: String(e.days) }), h('span', { text: ' d' })),
        h(
          'div',
          { class: 'eot__body' },
          h('div', { class: 'eot__reason', text: e.reason }),
          h('div', { class: 'muted', text: `Claimed ${prettyDate(e.raisedAt.slice(0, 10))} · ${e.raisedBy ?? 'not identified'}${ifGranted}` }),
          e.decidedBy
            ? h('div', { class: 'muted', text: `${EXTENSION_STATE_LABEL[e.state]} ${prettyDate(e.decidedAt?.slice(0, 10))} · ${e.decidedBy}` })
            : null,
        ),
        h(
          'div',
          { class: 'eot__state' },
          tag(EXTENSION_STATE_LABEL[e.state], e.state === 'accepted' ? 1 : e.state === 'proposed' ? 3 : 'none'),
          e.state === 'proposed' && mayApprove
              ? h(
                  'span',
                  { class: 'prog__acts' },
                  button('Accept', {
                    sm: true,
                    disabled: mine(s, e.raisedBy),
                    title: mine(s, e.raisedBy) ? 'You raised this, so somebody else must decide it.' : `Grant ${e.days} days`,
                    onClick: () => void actions.decideExtension(e.id, 'accept'),
                  }),
                  button('Reject', {
                    sm: true,
                    disabled: mine(s, e.raisedBy),
                    onClick: () => void actions.decideExtension(e.id, 'reject'),
                  }),
                )
              : null,
        ),
      );
    }),
  );
}

/* --------------------------- the history --------------------------- */

function historyPanel(s: Readonly<State>): HTMLElement {
  const inForce = s.baseline?.id ?? null;
  return panel(
    { title: 'Baseline history', meta: String(s.baselineHistory.length), collapsible: 'baseline.history' },
    h(
      'table',
      { class: 'tbl', 'data-history': String(s.baselineHistory.length) },
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { style: 'padding-left:14px', text: 'Baseline' }),
          h('th', { text: 'Taken' }),
          h('th', { text: 'Reason' }),
          h('th', { text: 'Accepted' }),
        ),
      ),
      h(
        'tbody',
        {},
        ...s.baselineHistory.map((b) =>
          h(
            'tr',
            { 'data-state': b.state ?? 'accepted' },
            h(
              'td',
              { style: 'padding-left:14px' },
              h('div', { text: b.name }),
              b.id === inForce
                ? tag('In force', 1)
                : (b.state ?? 'accepted') === 'proposed'
                  ? tag(b.id === s.proposedBaseline?.id ? 'Proposed' : 'Overtaken', 3)
                  : tag('Superseded', 'none'),
            ),
            h('td', {}, h('div', { text: prettyDate(b.takenAt.slice(0, 10)) }), h('div', { class: 'muted', text: b.takenBy ?? 'not identified' })),
            h('td', { text: b.reason ?? '—' }),
            h(
              'td',
              {},
              b.acceptedAt || (b.state ?? 'accepted') === 'accepted'
                ? [
                    h('div', { text: b.acceptedAt ? prettyDate(b.acceptedAt.slice(0, 10)) : '—' }),
                    h('div', { class: 'muted', text: b.acceptedBy ?? '' }),
                  ]
                : h('span', { class: 'muted', text: '—' }),
            ),
          ),
        ),
      ),
    ),
  );
}
